import { renderBatchEditCampaignTitle } from 'components/BatchEditCampaign/BatchEditCampaignTitle';
import { ModalState } from 'containers/Common/ModalStateFactory';
import { LimitationInventorySettings } from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import i18n from 'i18n';
import { ModifyLimitationStateContent } from './ModifyLimitationStateContent';
import {
  useAddLimitationStateContentModel,
  useRemoveLimitationStateContentModel
} from './ModifyLimitationStateContentModel';
import { LimitationSummaryStateContent } from './LimitationSummaryStateContent';
import {
  useAddLimitationSummaryStateContentModel,
  useRemoveLimitationSummaryStateContentModel
} from './LimitationSummaryStateContentModel';
import { LocaleMeta } from 'core';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { Order } from 'core/order/Order';
import {
  LimitationManagementContext,
  RtbCampaignContext
} from './LimitationManagementContext';

abstract class BasicLimitationManagementState {
  back?: () => void;
  next?: () => void;
  close?: (dirty: boolean, beforeClose?: () => Promise<void>) => void;

  constructor(
    protected campaigns: {
      id: number;
      name: string;
      adType: string;
      adTypeId?: number;
      limitations: LimitationData;
    }[],
    protected getContextData: () => LimitationManagementContext | undefined
  ) {}

  get renderTitle() {
    return () => renderBatchEditCampaignTitle(this.campaigns);
  }

  setBackFn = (back: () => void) => {
    this.back = back;
  };

  setNextFn = (next: () => void) => {
    this.next = next;
  };

  setCloseFn = (close: (dirty: boolean) => void) => {
    this.close = close;
  };

  canGoNext = () => {
    return true;
  };
}

export class AddLimitationState
  extends BasicLimitationManagementState
  implements ModalState
{
  constructor(
    protected order: Order,
    campaigns: RtbCampaignContext[],
    protected limitationModel: EditLimitationModel,
    protected supportAgeGenderLimitation: boolean,
    getContextData: () => LimitationManagementContext | undefined
  ) {
    super(campaigns, getContextData);
    this.next = async () => {
      this.close && this.close(true);
    };
  }

  get content() {
    return ModifyLimitationStateContent;
  }

  get contentModel() {
    return () =>
      useAddLimitationStateContentModel(
        this.order,
        this.campaigns,
        this.limitationModel,
        this.supportAgeGenderLimitation
      );
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.goSummary'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }

  canGoNext = () => {
    if (Object.keys(this.limitationModel.errors).length > 0) {
      return false;
    }

    const limitationManagementContext: LimitationManagementContext | undefined =
      this.getContextData();
    if (!limitationManagementContext) {
      return false;
    }
    for (const limitationOp of Object.keys(
      limitationManagementContext.limitations
    )) {
      const limitation: Limitation[] =
        limitationManagementContext.limitations[limitationOp];
      if (limitation.length > 0) {
        return true;
      }
    }
    return false;
  };
}

export class RemoveLimitationState extends AddLimitationState {
  constructor(
    order: Order,
    campaigns: RtbCampaignContext[],
    limitationModel: EditLimitationModel,
    getContextData: () => LimitationManagementContext | undefined
  ) {
    super(order, campaigns, limitationModel, false, getContextData);
  }

  override get contentModel() {
    return () =>
      useRemoveLimitationStateContentModel(
        this.order,
        this.campaigns,
        this.limitationModel
      );
  }
}

export class AddLimitationSummaryState
  extends BasicLimitationManagementState
  implements ModalState
{
  constructor(
    campaigns: RtbCampaignContext[],
    protected limitationInventorySettings: LimitationInventorySettings[],
    protected supportAgeGenderLimitation: boolean,
    getContextData: () => LimitationManagementContext | undefined,
    protected localeMeta?: LocaleMeta,
    protected campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    super(campaigns, getContextData);
    this.next = async () => {
      const limitationManagementContext = this.getContextData();
      if (!limitationManagementContext) {
        throw new Error('Limitation context is not initialized');
      }

      const { campaignIds, limitations } = limitationManagementContext;
      if (campaignIds.length === 0) {
        this.close && this.close(true);
        return;
      }

      this.close &&
        this.close(true, () =>
          this.campaignManager.batchAddLimitations(
            this.campaigns[0].adType,
            this.campaigns[0].adTypeId,
            campaignIds,
            limitations
          )
        );
    };
  }

  get content() {
    return LimitationSummaryStateContent;
  }

  get contentModel() {
    return () =>
      useAddLimitationSummaryStateContentModel(
        this.campaigns,
        this.limitationInventorySettings,
        this.supportAgeGenderLimitation,
        this.localeMeta
      );
  }

  get btnData() {
    const closeFn = this.close;
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.ok'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: closeFn && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          closeFn(false);
        },
        variant: 'secondary'
      }
    };
  }
}

export class RemoveLimitationSummaryState extends AddLimitationSummaryState {
  constructor(
    campaigns: RtbCampaignContext[],
    limitationInventorySettings: LimitationInventorySettings[],
    supportAgeGenderLimitation: boolean,
    getContextData: () => LimitationManagementContext | undefined,
    localeMeta?: LocaleMeta
  ) {
    super(
      campaigns,
      limitationInventorySettings,
      supportAgeGenderLimitation,
      getContextData,
      localeMeta
    );
    this.next = async () => {
      const limitationManagementContext = this.getContextData();
      if (!limitationManagementContext) {
        throw new Error('Limitation context is not initialized');
      }

      const { campaignIds, limitations } = limitationManagementContext;
      if (campaignIds.length === 0) {
        this.close && this.close(true);
        return;
      }

      this.close &&
        this.close(true, () =>
          this.campaignManager.batchRemoveLimitations(
            this.campaigns[0].adType,
            campaignIds,
            limitations
          )
        );
    };
  }

  override get contentModel() {
    return () =>
      useRemoveLimitationSummaryStateContentModel(
        this.campaigns,
        this.limitationInventorySettings,
        this.supportAgeGenderLimitation,
        this.localeMeta
      );
  }
}
